import type React from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';
import { useFormContext } from 'react-hook-form';

import { useI18n } from '@change-corgi/core/react/i18n';

import type { FocusedField } from 'src/app/shared/components/endorsements';

import { NAME_MAX_LENGTH, STATEMENT_MAX_LENGTH, TITLE_MAX_LENGTH } from '../../shared/validation';

import { PhotoUploadSection } from './components/PhotoUploadSection';
import { TextFieldWithCounter } from './TextFieldWithCounter';

type EndorsementFormFieldsProps = {
	statementCharCount: number;
	uppy: Uppy<Meta, Body>;
	image: string | undefined;
	onFieldFocus: (field: FocusedField) => void;
	onFieldBlur: () => void;
};

export const EndorsementFormFields: React.FC<EndorsementFormFieldsProps> = ({
	statementCharCount,
	uppy,
	image,
	onFieldFocus,
	onFieldBlur,
}) => {
	const { translate } = useI18n();
	const formContext = useFormContext();

	// Photo validation - read from Zod errors just like all other fields
	const photoError = formContext.formState.errors.photoUrl;
	const photoErrorMessage = typeof photoError?.message === 'string' ? photoError.message : undefined;

	return (
		<>
			<TextFieldWithCounter
				name="name"
				label={translate('corgi.petition.starter_dashboard_v2.endorsements.add.name_label')}
				supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.name_text_support')}
				maxLength={NAME_MAX_LENGTH}
				testId="endorser-name-field"
				qaId="endorser-name-field"
				onFocus={() => onFieldFocus('name')}
				onBlur={onFieldBlur}
			/>

			<TextFieldWithCounter
				name="title"
				label={translate('corgi.petition.starter_dashboard_v2.endorsements.add.role_or_description_label')}
				supportingText={translate(
					'corgi.petition.starter_dashboard_v2.endorsements.add.role_or_description_text_support',
				)}
				maxLength={TITLE_MAX_LENGTH}
				testId="endorser-title-field"
				qaId="endorser-title-field"
				onFocus={() => onFieldFocus('title')}
				onBlur={onFieldBlur}
			/>

			<TextFieldWithCounter
				name="statement"
				inputElementType="textarea"
				label={`${translate('corgi.petition.starter_dashboard_v2.endorsements.add.statement_support_label')} ${translate('corgi.petition.starter_dashboard_v2.endorsements.add.optional_label')}`}
				supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.statement_text_support')}
				maxLength={STATEMENT_MAX_LENGTH}
				currentLength={statementCharCount}
				inputClassName="min-h-28"
				testId="endorser-statement-field"
				qaId="endorser-statement-field"
				onFocus={() => onFieldFocus('statement')}
				onBlur={onFieldBlur}
			/>

			{/* Photo Upload Section */}
			<PhotoUploadSection image={image} uppy={uppy} isInvalid={!!photoError} errorMessage={photoErrorMessage} />

			<TextFieldWithCounter
				name="url"
				label={translate('corgi.petition.starter_dashboard_v2.endorsements.add.profile_or_link_label')}
				supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.profile_or_link_support_text')}
				testId="endorser-url-field"
				qaId="endorser-url-field"
				displayCounter={false}
				onFocus={() => onFieldFocus('url')}
				onBlur={onFieldBlur}
			/>
		</>
	);
};
